import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import apiClient from '../../api/client';
import chatService from '../../services/chatService';
import './TaxDocumentProcessor.css';
import './ChatBot.css';
import robinhoodLogo from '../../assets/Robinhood_Logo.png';
import etradeLogo from '../../assets/Etrade_Logo.png';
import fidelityLogo from '../../assets/Fidelity_Logo.png';
import schwabLogo from '../../assets/Schwab_Logo.png';


const TaxDocumentProcessor = ({ onClose, onComplete }) => {
    const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'processing', 'email', 'complete'
    const [uploadedFile, setUploadedFile] = useState(null);
    const [detectedBroker, setDetectedBroker] = useState(null);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingStatus, setProcessingStatus] = useState('');
    const [analysisResult, setAnalysisResult] = useState(null);
    const [userEmail, setUserEmail] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useRef(null);
    const fileUploadRef = useRef(null);

    const detectBrokerFromFileName = (fileName) => {
        const name = fileName.toLowerCase();
        if (name.includes('schwab') || name.includes('charles')) return 'schwab';
        if (name.includes('fidelity')) return 'fidelity';
        if (name.includes('etrade') || name.includes('e-trade')) return 'etrade';
        if (name.includes('robinhood')) return 'robinhood';
        if (name.includes('td') || name.includes('ameritrade')) return 'td_ameritrade';
        return null;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleFileUpload = (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        // Validate file type
        const allowedTypes = ['application/pdf', 'text/csv', 'application/vnd.ms-excel'];
        if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.csv')) {
            toast.current.show({
                severity: 'error',
                summary: 'Invalid File Type',
                detail: 'Only PDF and CSV files are allowed.',
                life: 3000
            });
            return;
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            toast.current.show({
                severity: 'error',
                summary: 'File Too Large',
                detail: `${file.name} is too large. Maximum size is 10MB.`,
                life: 3000
            });
            return;
        }

        // Detect broker
        const broker = detectBrokerFromFileName(file.name);
        setDetectedBroker(broker);

        const uploadedFileObj = {
            file: file,
            name: file.name,
            size: file.size,
            type: file.type
        };

        setUploadedFile(uploadedFileObj);

        toast.current.show({
            severity: 'success',
            summary: 'Document Uploaded',
            detail: `${file.name} ready for processing${broker ? ` (${broker} detected)` : ''}`,
            life: 3000
        });
    };

    const simulateProcessing = () => {
        setCurrentStep('processing');
        setProcessingProgress(0);
        setProcessingStatus('Initializing document analysis...');

        const steps = [
            { progress: 20, status: 'Reading document structure...' },
            { progress: 40, status: 'Extracting transaction data...' },
            { progress: 60, status: 'Analyzing tax implications...' },
            { progress: 80, status: 'Calculating optimization opportunities...' },
            { progress: 100, status: 'Analysis complete!' }
        ];

        let currentStepIndex = 0;
        const interval = setInterval(() => {
            if (currentStepIndex < steps.length) {
                const step = steps[currentStepIndex];
                setProcessingProgress(step.progress);
                setProcessingStatus(step.status);
                currentStepIndex++;
            } else {
                clearInterval(interval);
                setTimeout(() => {
                    setCurrentStep('email');
                }, 1000);
            }
        }, 1500);
    };

    const processDocument = async () => {
        if (!uploadedFile) return;

        setIsLoading(true);
        simulateProcessing();

        try {
            const username = localStorage.getItem('username');
            const response = await chatService.sendMessageWithDocument(
                'Please provide a comprehensive tax analysis of this document',
                username,
                uploadedFile.file,
                detectedBroker
            );

            setAnalysisResult(response);
        } catch (error) {
            console.error('Error processing document:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Processing Error',
                detail: 'File size is too large. Please try again with a smaller file.',
                life: 3000
            });
            setCurrentStep('upload');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailChange = (e) => {
        const email = e.target.value;
        setUserEmail(email);
        setIsEmailValid(validateEmail(email));
    };

    const sendDetailedAnalysis = async () => {
        if (!isEmailValid || !uploadedFile) return;

        setIsLoading(true);
        try {
            // Create FormData for the API call (this is correct for @RequestParam with file upload)
            const formData = new FormData();
            formData.append('email', userEmail);
            formData.append('file', uploadedFile.file);
            if (detectedBroker) {
                formData.append('broker', detectedBroker);
            }


            // Call the real API endpoint using axios like Accounts.js
            // For multipart with @RequestParam, we need to send the file in FormData and other params as URL params
            const response = await apiClient.post('/api/v1/chatbot/send-analysis', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });


            const result = response.data;

            if (result.success !== false) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Analysis Sent',
                    detail: `Detailed tax analysis has been sent to ${userEmail}`,
                    life: 5000
                });

                setCurrentStep('complete');
                
                // Auto-close after showing success
                setTimeout(() => {
                    onComplete && onComplete(result);
                }, 3000);
            } else {
                throw new Error(result.error || 'Failed to send analysis');
            }

        } catch (error) {
            console.error('Error sending analysis:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Send Error',
                detail: error.message || 'Failed to send analysis. Please try again.',
                life: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const renderUploadStep = () => (
        <div className="tax-processor-step">
            <div className="step-header">
                <i className="pi pi-upload" style={{ fontSize: '2rem', color: '#2A9D8F' }}></i>
                <h3>Upload Tax Document</h3>
                <p>
                    Upload your brokerage tax form - <strong className="highlight">1099</strong>, which you can typically find in your brokerage app under "Tax Documents."
                </p>
                <div className="supported-brokers-section">
                  <div className="supported-brokers-title">Primary Supported Brokerages:</div>
                  <div className="supported-brokers-logos">
                    <div className="broker-logo-card">
                      <img src={robinhoodLogo} alt="Robinhood" />
                      <span>Robinhood</span>
                    </div>
                    <div className="broker-logo-card">
                      <img src={etradeLogo} alt="E*TRADE" />
                      <span>E*TRADE</span>
                    </div>
                    <div className="broker-logo-card">
                      <img src={fidelityLogo} alt="Fidelity" />
                      <span>Fidelity</span>
                    </div>
                    <div className="broker-logo-card">
                      <img src={schwabLogo} alt="Schwab" />
                      <span>Schwab</span>
                    </div>
                  </div>
                  <div className="supported-brokers-note">
                    We do support other brokerages too, but accuracy might differ a bit.
                  </div>
                </div>
                <div className="privacy-guarantee">
                    <i className="pi pi-lock"></i>
                    <div className="privacy-text">
                        <strong>Your privacy is our top priority.</strong>
                        <span>We like to respect your privacy the same way we like ours respected.</span>
                        <span>Documents are processed for analysis and are never stored.</span>
                    </div>
                </div>
            </div>

            <div className="upload-area">
                <input
                    type="file"
                    ref={fileUploadRef}
                    accept=".pdf,.csv"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
                
                {!uploadedFile ? (
                    <div 
                        className="upload-dropzone"
                        onClick={() => fileUploadRef.current?.click()}
                    >
                        <i className="pi pi-cloud-upload" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                        <p>Click to upload or drag and drop</p>
                        <small>1099-B PDF or CSV files up to 5MB</small>
                    </div>
                ) : (
                    <div className="uploaded-file-info">
                        <div className="file-details">
                            <Tag 
                                icon="pi pi-file"
                                value={`${uploadedFile.name} (${formatFileSize(uploadedFile.size)})`}
                                severity="success"
                            />
                            {detectedBroker && (
                                <Tag 
                                    value={detectedBroker.toUpperCase()}
                                    severity="info"
                                />
                            )}
                        </div>
                        <div className="file-actions">
                            <Button 
                                label="Change File"
                                icon="pi pi-refresh"
                                className="p-button-text"
                                onClick={() => fileUploadRef.current?.click()}
                            />
                            <Button 
                                label="Process Document"
                                icon="pi pi-arrow-right"
                                iconPos="left"
                                onClick={processDocument}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderProcessingStep = () => (
        <div className="tax-processor-step">
            <div className="step-header">
                <i className="pi pi-spin pi-cog" style={{ fontSize: '2rem', color: '#2A9D8F' }}></i>
                <h3>Processing Document</h3>
                <p>Analyzing your tax document for optimization opportunities</p>
            </div>

            <div className="processing-content">
                <ProgressBar value={processingProgress} className="processing-bar" />
                <p className="processing-status">{processingStatus}</p>
                
                <div className="processing-info">
                    <div className="info-item">
                        <strong>Document:</strong> {uploadedFile?.name}
                    </div>
                    {detectedBroker && (
                        <div className="info-item">
                            <strong>Broker:</strong> {detectedBroker.toUpperCase()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderEmailStep = () => (
        <div className="tax-processor-step email-step">
            <div className="step-header">
                <i className="pi pi-envelope" style={{ fontSize: '2rem', color: '#2A9D8F' }}></i>
                <h3>Get Detailed Analysis</h3>
                <p>Enter your email to receive a comprehensive tax analysis report</p>
            </div>

            <div className="email-content">
                <div className="analysis-preview">
                    <h4>Analysis Complete!</h4>
                    <p>We've processed your tax document and identified several optimization opportunities.</p>
                    
                    <div className="preview-highlights">
                        <div className="highlight-item">
                            <i className="pi pi-check-circle" style={{ color: '#28a745' }}></i>
                            <span>Tax loss harvesting opportunities identified</span>
                        </div>
                        <div className="highlight-item">
                            <i className="pi pi-check-circle" style={{ color: '#28a745' }}></i>
                            <span>Wash sale violations detected and flagged</span>
                        </div>
                        <div className="highlight-item">
                            <i className="pi pi-check-circle" style={{ color: '#28a745' }}></i>
                            <span>Portfolio optimization recommendations generated</span>
                        </div>
                    </div>
                </div>

                <Divider />

                <div className="email-input-section">
                    <label htmlFor="email">Email Address</label>
                    <div className="email-input-container">
                        <InputText
                            id="email"
                            value={userEmail}
                            onChange={handleEmailChange}
                            placeholder="Enter your email address"
                            className={!isEmailValid && userEmail ? 'p-invalid' : ''}
                        />
                        <Button
                            label="Send Analysis"
                            icon="pi pi-send"
                            iconPos="left"
                            onClick={sendDetailedAnalysis}
                            disabled={!isEmailValid || isLoading}
                            loading={isLoading}
                        />
                    </div>
                    {!isEmailValid && userEmail && (
                        <small className="p-error">Please enter a valid email address</small>
                    )}
                </div>
            </div>
        </div>
    );

    const renderCompleteStep = () => (
        <div className="tax-processor-step">
            <div className="step-header">
                <i className="pi pi-check-circle" style={{ fontSize: '2rem', color: '#28a745' }}></i>
                <h3>Analysis Sent!</h3>
                <p>Your detailed tax analysis has been sent to {userEmail}</p>
            </div>

            <div className="complete-content">
                <div className="success-message">
                    <p>Check your email for a comprehensive report including:</p>
                    <ul>
                        <li>Detailed transaction analysis</li>
                        <li>Tax optimization strategies</li>
                        <li>Wash sale identification</li>
                        <li>Portfolio recommendations</li>
                        <li>Potential tax savings calculations</li>
                    </ul>
                </div>
                
                <Button 
                    label="Close"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={onClose}
                />
            </div>
        </div>
    );

    const getCurrentStepContent = () => {
        switch (currentStep) {
            case 'upload':
                return renderUploadStep();
            case 'processing':
                return renderProcessingStep();
            case 'email':
                return renderEmailStep();
            case 'complete':
                return renderCompleteStep();
            default:
                return renderUploadStep();
        }
    };

    return (
        <Dialog
            visible={true}
            onHide={onClose}
            header="Tax Document Analysis"
            style={{ width: '600px' }}
            modal
            closable={currentStep !== 'processing'}
        >
            <Toast ref={toast} />
            <div className="tax-document-processor">
                {getCurrentStepContent()}
            </div>
        </Dialog>
    );
};

export default TaxDocumentProcessor; 